import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faRedo, faTrashCan, faSearch } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import projectApi from '../../api/projectApi';

const TrashedProjects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [projectToActOnId, setProjectToActOnId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchTrashedProjects = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await projectApi.getProjects({ page, perPage, search, includeTrashed: true });
      setProjects(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch trashed projects:', err);
      setError('Failed to load trashed projects. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrashedProjects(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchTrashedProjects, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleRestoreClick = (id) => {
    setProjectToActOnId(id);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    setIsRestoreModalOpen(false);
    if (projectToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await projectApi.restoreProject(projectToActOnId);
        await fetchTrashedProjects(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to restore project:', err);
        setError('Failed to restore project. Please try again.');
      } finally {
        setLoading(false);
        setProjectToActOnId(null);
      }
    }
  };

  const handlePermanentDeleteClick = (id) => {
    setProjectToActOnId(id);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    setIsPermanentDeleteModalOpen(false);
    if (projectToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await projectApi.permanentDeleteProject(projectToActOnId);
        await fetchTrashedProjects(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to permanently delete project:', err);
        setError('Failed to permanently delete project. Please try again.');
      } finally {
        setLoading(false);
        setProjectToActOnId(null);
      }
    }
  };

  const handleCancelAction = () => {
    setIsRestoreModalOpen(false);
    setIsPermanentDeleteModalOpen(false);
    setProjectToActOnId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Trashed Projects</h1>
        <Button
          onClick={() => navigate('/projects')}
          className="bg-accent-red hover:bg-red-700 text-white"
        >
          Back to Projects
        </Button>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Trashed Projects"
          id="search-trashed-projects"
          name="search-trashed-projects"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by title, description or technologies..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Image</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Title</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Description</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.length > 0 ? (
                  projects.map((project) => (
                    <tr key={project.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{project.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {project.image ? (
                          <img src={project.image} alt={project.title} className="h-10 w-10 object-cover" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-600 flex items-center justify-center text-xs">No Img</div>
                        )}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{project.name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">{project.excerpt}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleRestoreClick(project.id)}
                          className="text-green-500 hover:text-green-600 mr-3"
                          icon={faRedo}
                          tooltipId={`restore-project-tooltip-${project.id}`}
                          tooltipContent="Restore"
                        />
                        <Button
                          onClick={() => handlePermanentDeleteClick(project.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrashCan}
                          tooltipId={`permanent-delete-project-tooltip-${project.id}`}
                          tooltipContent="Permanent Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-sm text-text-gray">No trashed projects found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        message="Are you sure you want to restore this project?"
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmPermanentDelete}
        title="Confirm Permanent Delete"
        message="Are you sure you want to permanently delete this project? This action cannot be undone."
      />
    </div>
  );
};

export default TrashedProjects;